import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_5ecc19a3-715e-40b2-bee7-8ae4aeb4c8cb/artifacts/ladwjbmi_4926BBCA-F27B-484F-9467-294F35FF2347.PNG';

const navLinks = [
  { path: '/about', label: 'About' },
  { path: '/capabilities', label: 'Capabilities' },
  { path: '/contact', label: 'Contact' },
];

export default function Layout({ children }) {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    // Scroll to top on route change
    window.scrollTo(0, 0);
    // Also reset Lenis scroll position
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location]);

  return (
    <div className="relative min-h-screen bg-xmx-black text-xmx-white xmx-grain overflow-x-hidden">
      {/* ── TOP-LEFT: Logo ── */}
      <Link
        to="/"
        data-testid="nav-home"
        className="fixed top-5 left-5 sm:top-7 sm:left-8 z-50 mix-blend-difference"
      >
        <motion.img
          src={LOGO_URL}
          alt="XMX Ventures"
          className="h-[74px] sm:h-[92px] lg:h-[110px] w-auto"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
        />
      </Link>

      {/* ── TOP-RIGHT: Navigation ── */}
      <nav className="fixed top-8 right-6 sm:top-10 sm:right-8 z-50 mix-blend-difference">
        {/* Desktop nav */}
        <motion.div
          className="hidden sm:flex items-center gap-8"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-${link.label.toLowerCase()}`}
              className={`nav-link font-mono text-xs tracking-[0.2em] uppercase transition-colors duration-300 ${
                location.pathname === link.path
                  ? 'text-xmx-white'
                  : 'text-xmx-grey hover:text-xmx-white'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </motion.div>

        {/* Mobile hamburger */}
        <motion.button
          data-testid="mobile-menu-toggle"
          className="sm:hidden flex flex-col gap-1.5 p-2"
          onClick={() => setMenuOpen(!menuOpen)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <motion.span
            className="block w-6 h-[1px] bg-xmx-white origin-center"
            animate={menuOpen ? { rotate: 45, y: 4 } : { rotate: 0, y: 0 }}
            transition={{ duration: 0.3 }}
          />
          <motion.span
            className="block w-6 h-[1px] bg-xmx-white"
            animate={menuOpen ? { opacity: 0 } : { opacity: 1 }}
            transition={{ duration: 0.2 }}
          />
          <motion.span
            className="block w-6 h-[1px] bg-xmx-white origin-center"
            animate={menuOpen ? { rotate: -45, y: -4 } : { rotate: 0, y: 0 }}
            transition={{ duration: 0.3 }}
          />
        </motion.button>
      </nav>

      {/* ── Mobile Menu Overlay ── */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-40 bg-xmx-black/98 flex items-center justify-center sm:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="flex flex-col items-center gap-10">
              {[{ path: '/', label: 'Home' }, ...navLinks].map((link, i) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: i * 0.08, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Link
                    to={link.path}
                    data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                    className="font-cabinet text-3xl tracking-tight uppercase text-xmx-white hover:text-xmx-accent transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── BOTTOM-LEFT: Status ── */}
      <motion.div
        className="fixed bottom-6 left-6 sm:bottom-8 sm:left-8 z-50 mix-blend-difference hidden sm:block"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: scrolled ? 1 : 0.4, y: 0 }}
        transition={{ duration: 0.8, delay: 1, ease: [0.16, 1, 0.3, 1] }}
      >
        <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-xmx-grey">
          Operating at scale
        </span>
      </motion.div>

      {/* ── BOTTOM-RIGHT: Scroll indicator ── */}
      <motion.div
        className="fixed bottom-6 right-6 sm:bottom-8 sm:right-8 z-50 mix-blend-difference hidden sm:flex items-center gap-3"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: scrolled ? 0 : 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
      >
        <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-xmx-grey">
          Scroll
        </span>
        <motion.div
          className="w-[1px] h-8 bg-xmx-grey/40 relative overflow-hidden"
        >
          <motion.div
            className="absolute top-0 left-0 w-full bg-xmx-accent"
            animate={{ height: ['0%', '100%', '0%'] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          />
        </motion.div>
      </motion.div>

      {/* ── Page Content ── */}
      <AnimatePresence mode="wait">
        <motion.main
          key={location.pathname}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          {children}
        </motion.main>
      </AnimatePresence>
    </div>
  );
}
